import React, { useMemo } from 'react';

import { FormControl, FormField, FormItem, FormMessage } from '@crossnokaye/ux-common';

import { SettingFieldProps } from '@/features/equipment-browser/equipment-details/settings/components/setting-field.types';
import { SettingFieldLabelRow } from '@/features/equipment-browser/equipment-details/settings/components/setting-field-label-row';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { InputValueChange } from '@/ux-common/components/input/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/ux-common/components/select/select';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';

const SelectTriggerWithTooltip = withTooltip(SelectTrigger);

/**
 * Dropdown field component for enum/select settings.
 * Displays a list of options for selection.
 */
export const SettingFieldDropdown = ({
  form,
  name,
  setting,
}: SettingFieldProps): React.ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  // Radix THROWS on a SelectItem with an empty value — it reserves '' for "no
  // selection" — and an error thrown while rendering takes the whole Settings
  // panel with it, not just this field. displayOptions comes from the platform and
  // nothing upstream guarantees a value, so a blank one is dropped here. A missing
  // row is a better failure than a blank panel, and a choice with no value could
  // not be selected anyway. The legacy DropdownMenu tolerated it by rendering a
  // dead row, which is why this is newly fatal rather than newly wrong.
  const options = useMemo(
    () => (setting.displayOptions ?? []).filter((option) => option.value !== ''),
    [setting.displayOptions]
  );
  const hasChanged = useFieldIsDirty(form.control, name);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // The Select's value must be the option's `value` verbatim — Radix matches
        // SelectItem by exact string — so there is nothing to resolve: a value that
        // matches an option already IS that option's value, and one that matches
        // none shows the placeholder. The previous lookup found the option and then
        // used the raw value anyway, which read as if matching happened here.
        const currentValue = field.value?.toString() || '';
        const originalValue = setting.value?.toString();

        // One copy of the children for both branches below. The trigger matches
        // `InputValueChange` among its children to hoist it out of the truncating
        // value span; a fragment is safe because it looks through fragments, but a
        // custom wrapper component would hide it from that match. With no value
        // both are falsy, so Radix's own placeholder shows.
        const triggerChildren = (
          <>
            {currentValue ? currentValue.toUpperCase() : undefined}
            {currentValue && hasChanged ? (
              <InputValueChange oldValue={originalValue?.toUpperCase() ?? ''} />
            ) : undefined}
          </>
        );

        return (
          <FormItem data-component="SettingFieldDropdown">
            {/* `new`, not `dirty`: this is a control value where the previous one
                matters, so the field shows the `← oldValue` lockup as well as the
                staged border. See the Input archetypes. */}
            <Select
              disabledReason={disabledByPermissions ? tooltipText : undefined}
              onValueChange={field.onChange}
              state={hasChanged ? 'new' : undefined}
              value={currentValue}
            >
              <SettingFieldLabelRow description={setting.description} label={setting.label} />
              {/* `FormControl` is a Radix `Slot` and must wrap the control itself —
                  the trigger is the focusable element, so `id` and `aria-invalid`
                  land there rather than on a wrapper. */}
              <FormControl>
                {/* Wrapped unconditionally — see `setting-field-boolean.tsx` for why
                    a conditional wrapper remounts the trigger. */}
                <SelectTriggerWithTooltip
                  // `FormControl` always emits `aria-describedby`, pointing at
                  // `formDescriptionId` — and no `FormDescription` is rendered here
                  // or anywhere in gaia, so it resolves to nothing. Passing
                  // undefined lets the group supply its own: the refusal reason and
                  // any `InputHelper`, which are real. Child props win over the
                  // Slot's.
                  aria-describedby={undefined}
                  placeholder={'Select an option'}
                  tooltipArrow
                  tooltipContent={disabledByPermissions ? tooltipText : undefined}
                >
                  {triggerChildren}
                </SelectTriggerWithTooltip>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
